import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Search, Check, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';
import { loadGoogleMaps } from '../components/AddressAutocomplete';

const LocationPicker = lazy(() => import('../components/LocationPicker'));

const GAPS = [
  { key: 'address', label: 'No address',  hint: 'Prints as a name and a phone number only — the driver cannot find it' },
  { key: 'pin',     label: 'No map pin',  hint: 'Cannot be put in route order, on the sheet or in the app' },
  { key: 'town',    label: 'No town',     hint: 'Cannot be grouped by area' },
];

/**
 * Work through the clients whose address is missing.
 *
 * Busiest first, because fixing the client who gets a parcel every week is
 * worth more than one who has never had a single delivery. Each row can ask
 * Google for a match on the client's own name — most of these are businesses in
 * small towns, and "Fish Fever, Gansbaai" finds itself even when nobody has
 * ever typed a street address.
 */
export default function MissingAddresses() {
  const qc = useQueryClient();
  const [gap, setGap] = useState('address');
  const [search, setSearch] = useState('');
  const [openFor, setOpenFor] = useState(null);   // client id whose suggestions are showing
  const [preds, setPreds] = useState([]);
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(null);
  const [manual, setManual] = useState('');
  const [bulk, setBulk] = useState(null);        // result of "Pin from local data"
  const [pinFor, setPinFor] = useState(null);    // client id whose map is open
  const [pinPos, setPinPos] = useState(null);    // { lat, lng } chosen on that map
  const [pinSaving, setPinSaving] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const libRef = useRef(null);
  const tokenRef = useRef(null);

  const key = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  useEffect(() => {
    if (!key) return;
    loadGoogleMaps(key)
      .then(() => window.google.maps.importLibrary('places'))
      .then((lib) => { libRef.current = lib; })
      .catch(() => {});
  }, [key]);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients-missing', gap],
    queryFn: () => api.get('/clients', {
      params: { missing: gap, sort: 'busiest', limit: 2000 },
    }).then((r) => r.data),
  });

  const q = search.trim().toLowerCase();
  const rows = q ? clients.filter((c) => (c.name || '').toLowerCase().includes(q)) : clients;

  // Ask Google what it knows about this client. The query is the client's own
  // name plus whatever town we already have — that is exactly the search a
  // person would type.
  // Our own Overberg address data first (free, exact street numbers); Google
  // only when the local data has nothing — mostly clients outside the Overberg.
  const suggest = async (c, text) => {
    setOpenFor(c.id);
    setPreds([]);
    if (!text) { setManual(''); setPinFor(null); setPinPos(null); }   // opening a different client starts clean
    setLooking(true);
    try {
      const input = (text || c.address || `${c.name} ${c.city || ''}`).trim();
      let list = [];
      try {
        const { data } = await api.get('/geo/suggest', { params: { q: input, town: c.city || '' } });
        list = (data || []).map((row) => ({ kind: 'local', row }));
      } catch { list = []; }
      if (!list.length) {
        const lib = libRef.current;
        if (!lib) {
          toast(key ? 'Nothing in the local address data — Google is still loading, try again' : 'Nothing in the local address data, and no Google key is set — type it in by hand');
        } else {
          const { AutocompleteSessionToken, AutocompleteSuggestion } = lib;
          if (!tokenRef.current) tokenRef.current = new AutocompleteSessionToken();
          const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input, sessionToken: tokenRef.current, includedRegionCodes: ['za'],
          });
          list = (suggestions || []).map((sg) => ({ kind: 'google', suggestion: sg }));
          if (!list.length) toast('Nothing found — try adding the town, or type it in by hand');
        }
      }
      setPreds(list);
    } catch {
      toast.error('Could not look that up');
    } finally { setLooking(false); }
  };

  const accept = async (c, p) => {
    setSaving(c.id);
    try {
      let body;
      if (p.kind === 'local') {
        const r = p.row;
        body = { address: r.address, city: r.town || c.city || '', postal_code: r.postcode || c.postal_code || '', latitude: r.lat, longitude: r.lng };
      } else {
        const place = p.suggestion.placePrediction.toPlace();
        await place.fetchFields({ fields: ['formattedAddress', 'location', 'addressComponents'] });
        tokenRef.current = null;
        const comp = place.addressComponents || [];
        const get = (t) => comp.find((x) => x.types.includes(t))?.longText;
        body = {
          address: place.formattedAddress || '',
          city: get('locality') || get('administrative_area_level_2') || c.city || '',
          postal_code: get('postal_code') || c.postal_code || '',
          latitude: place.location.lat(),
          longitude: place.location.lng(),
        };
      }
      // PATCH /clients/:id only writes the fields present in the body, so send
      // just the location ones — everything else about the client is left alone.
      await api.patch(`/clients/${c.id}`, body);
      toast.success(`${c.name} — address and map pin saved`);
      setOpenFor(null); setPreds([]); setManual('');
      qc.invalidateQueries({ queryKey: ['clients-missing'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save that address');
    } finally { setSaving(null); }
  };

  // A pin dropped by hand on the map. The town is filled from the pin when the
  // client has none; whatever was typed in the search box becomes the address.
  const savePin = async (c) => {
    const lat = parseFloat(pinPos?.lat), lng = parseFloat(pinPos?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return toast.error('Click the map where the door is first');
    setPinSaving(true);
    try {
      const body = { latitude: lat, longitude: lng };
      const typed = manual.trim();
      if (typed) body.address = typed;
      if (!c.city) {
        try { const { data } = await api.get('/geo/suburb', { params: { lat, lng } }); if (data?.town) body.city = data.town; } catch { /* leave blank */ }
      }
      await api.patch(`/clients/${c.id}`, body);
      toast.success(`${c.name} — map pin saved${body.city ? ` (${body.city})` : ''}`);
      setPinFor(null); setPinPos(null); setOpenFor(null); setManual('');
      qc.invalidateQueries({ queryKey: ['clients-missing'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the pin');
    } finally { setPinSaving(false); }
  };

  const pinFromLocal = async (dry) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post('/geo/pin-missing', { dry, limit: 300 });
      setBulk({ ...data, dry });
      if (!dry) {
        toast.success(`${data.pinned.length} client${data.pinned.length === 1 ? '' : 's'} pinned from the address data`);
        qc.invalidateQueries({ queryKey: ['clients-missing'] });
        qc.invalidateQueries({ queryKey: ['clients'] });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not run the address data pass');
    } finally { setBulkBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <MapPin size={22} className="text-orange-500" />
        <div>
          <h1 className="text-xl font-bold text-gray-800">Missing addresses</h1>
          <p className="text-xs text-gray-400">
            Fill the gaps that stop a driver finding the door — busiest clients first
          </p>
        </div>
        <Link to="/clients" className="ml-auto text-xs text-orange-500 hover:underline">All clients</Link>
      </div>

      {/* The free pass: every client with an Overberg address but no pin */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-64">
            <p className="text-sm font-semibold text-gray-800">Pin from the local address data</p>
            <p className="text-xs text-gray-400">
              Clients with a street address in Gansbaai, Hermanus, Stanford, Bredasdorp, Kleinmond and the other
              Overberg towns get their map pin, town and postcode from the provincial address register — no Google.
              Existing pins are never changed.
            </p>
          </div>
          <button onClick={() => pinFromLocal(true)} disabled={bulkBusy}
            className="border border-gray-300 hover:border-orange-400 rounded-lg px-3 py-2 text-xs text-gray-700 disabled:opacity-40">
            {bulkBusy ? 'Working…' : 'Preview'}
          </button>
          <button onClick={() => pinFromLocal(false)} disabled={bulkBusy}
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 py-2 text-xs font-semibold disabled:opacity-40">
            Pin them
          </button>
        </div>
        {bulk && (
          <div className="mt-3 text-xs text-gray-600 space-y-1">
            <p>
              <strong>{bulk.dry ? 'Would pin' : 'Pinned'} {bulk.pinned.length}</strong> of {bulk.counts.checked} checked ·
              exact street number {bulk.counts['street+no'] || 0} · street only {bulk.counts.street || 0} · geocoder {bulk.counts.geocode || 0} ·
              no match {bulk.counts.none || 0} · outside the local towns {bulk.counts.not_local || 0}
            </p>
            {bulk.pinned.length > 0 && (
              <p className="text-gray-400">
                {bulk.pinned.slice(0, 12).map((x) => `${x.name} (${x.method}${x.suburb ? ', ' + x.suburb : ''})`).join(' · ')}
                {bulk.pinned.length > 12 ? ` · +${bulk.pinned.length - 12} more` : ''}
              </p>
            )}
            {bulk.counts.not_local > 0 && (
              <p className="text-gray-400">Clients outside the Overberg still need Google or a hand-placed pin — use Find address on each.</p>
            )}
          </div>
        )}
      </div>

      {!key && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">
            No Google Maps key is set. Addresses in the Overberg towns still come from the local data;
            for other towns you can type the address in by hand.
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-4 flex gap-3 flex-wrap items-center">
        <div className="flex gap-2">
          {GAPS.map((g) => (
            <button key={g.key} onClick={() => { setGap(g.key); setOpenFor(null); }}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                gap === g.key ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400'
              }`}>
              {g.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Find a client…"
            className="w-56 pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        <span className="text-sm text-gray-500 ml-auto">
          <strong className="text-orange-600">{rows.length}</strong> to fix
        </span>
      </div>

      <p className="text-xs text-gray-400">{GAPS.find((g) => g.key === gap)?.hint}</p>

      <div className="bg-white rounded-xl shadow-sm divide-y">
        {isLoading ? (
          <p className="p-8 text-center text-gray-400">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-gray-400">Nothing missing here — nicely done.</p>
        ) : rows.map((c) => (
          <div key={c.id} className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <Link to={`/clients/${c.id}`} className="font-semibold text-gray-800 hover:text-orange-600">
                  {c.name}
                </Link>
                <p className="text-xs text-gray-500">
                  {[c.city, c.phone].filter(Boolean).join(' · ') || 'no town, no phone'}
                  {c.address && <span className="text-gray-400"> · {c.address}</span>}
                </p>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${
                c.parcel_count > 0 ? 'bg-orange-50 text-orange-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {c.parcel_count} parcel{c.parcel_count === 1 ? '' : 's'}
              </span>
              <button onClick={() => (openFor === c.id ? setOpenFor(null) : suggest(c))}
                className="border border-gray-300 hover:border-orange-400 rounded-lg px-3 py-1.5 text-xs text-gray-700 disabled:opacity-40">
                {openFor === c.id ? 'Close' : 'Find address'}
              </button>
            </div>

            {openFor === c.id && (
              <div className="mt-3 border-2 border-orange-200 rounded-lg p-3 space-y-2">
                <div className="flex gap-2">
                  <input type="text" value={manual} onChange={(e) => setManual(e.target.value)}
                    placeholder={`Searched "${[c.name, c.city].filter(Boolean).join(' ')}" — type something else to change it`}
                    className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  <button onClick={() => suggest(c, manual)}
                    className="border rounded-lg px-3 text-sm text-gray-600 hover:bg-gray-50">Search</button>
                  <button onClick={() => { setPinFor(pinFor === c.id ? null : c.id); setPinPos(null); }}
                    className={`flex items-center gap-1 border rounded-lg px-3 text-sm whitespace-nowrap ${pinFor === c.id ? 'bg-orange-500 text-white border-orange-500' : 'text-gray-600 hover:bg-gray-50'}`}>
                    <MapPin size={14} /> {pinFor === c.id ? 'Hide map' : 'Drop a pin'}
                  </button>
                </div>

                {pinFor === c.id && (
                  <div className="border rounded-lg p-2 bg-white">
                    <p className="text-xs text-gray-500 mb-2">
                      Click the map where the driver should stop. Zoom in for the exact door; satellite view helps for farms and estates.
                    </p>
                    <Suspense fallback={<div className="h-56 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm">Loading map…</div>}>
                      <LocationPicker lat={pinPos?.lat} lng={pinPos?.lng}
                        defaultArea={/CAPE|BELLVILLE|PAROW|DURBANVILLE|SOMERSET|STRAND|STELLENBOSCH/i.test(c.city || '') ? 'capetown' : 'gansbaai'}
                        onChange={(p) => setPinPos(p)} />
                    </Suspense>
                    <div className="flex items-center justify-between gap-3 mt-2">
                      <span className="text-xs text-gray-400">
                        {pinPos?.lat ? `Pin at ${parseFloat(pinPos.lat).toFixed(5)}, ${parseFloat(pinPos.lng).toFixed(5)}` : 'No pin yet'}
                        {manual.trim() ? ' · the text in the box is saved as the address' : ''}
                      </span>
                      <button onClick={() => savePin(c)} disabled={pinSaving || !pinPos?.lat}
                        className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 py-2 text-xs font-semibold disabled:opacity-40">
                        {pinSaving ? 'Saving…' : 'Save pin'}
                      </button>
                    </div>
                  </div>
                )}

                {looking ? (
                  <p className="text-xs text-gray-400 py-2">Looking up…</p>
                ) : preds.length === 0 ? (
                  <p className="text-xs text-gray-400 py-2">
                    No match. Try the town or a nearby landmark, or press <strong>Drop a pin</strong> and click the map.
                  </p>
                ) : (
                  <ul className="border rounded-lg divide-y max-h-56 overflow-auto">
                    {preds.map((p, i) => (
                      <li key={i}>
                        <button onClick={() => accept(c, p)} disabled={saving === c.id}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 flex items-center gap-2 disabled:opacity-50">
                          <Check size={14} className="text-orange-500 flex-shrink-0" />
                          <span className="flex-1">{p.kind === 'local' ? p.row.label : (p.suggestion.placePrediction?.text?.text || '')}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${p.kind === 'local' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {p.kind === 'local' ? 'local' : 'Google'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[11px] text-gray-400">
                  Picking one saves the address, the town and the map pin — which is what puts this stop
                  in the right place in the driver&apos;s route.
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
